import { MotionReveal } from "../components/MotionReveal";
import { Layers, Quote } from "lucide-react";

const comparisonRows = [
  {
    dimension: "Core Question",
    observability: {
      title: "What is happening and why?",
      example: "Dashboards show rising API latency and error rates",
    },
    aiops: {
      title: "What should we do about it?",
      example: "AI recommends rollback or scale-out",
    },
  },
  {
    dimension: "Primary Purpose",
    observability: {
      title: "Visibility and understanding",
      example: "Engineers trace issue to downstream dependency",
    },
    aiops: {
      title: "Intelligent decision & action",
      example: "AI isolates root cause and initiates fix",
    },
  },
  {
    dimension: "Nature",
    observability: {
      title: "Foundational capability",
      example: "Collects logs, metrics, traces",
    },
    aiops: {
      title: "Intelligence & automation layer",
      example: "ML reasons across telemetry",
    },
  },
  {
    dimension: "Data Sources",
    observability: {
      title: "Logs, metrics, traces, events, packets",
      example: "Unified telemetry pipeline",
    },
    aiops: {
      title: "Uses observability data",
      example: "Correlates multi-source signals",
    },
  },
  {
    dimension: "Scale Handling",
    observability: {
      title: "Human-limited",
      example: "Experts interpret large dashboards",
    },
    aiops: {
      title: "Built for scale",
      example: "AI handles millions of events",
    },
  },
  {
    dimension: "Analysis Type",
    observability: {
      title: "Human-led",
      example: "Manual log and trace analysis",
    },
    aiops: {
      title: "Machine-led",
      example: "AI-driven correlation",
    },
  },
  {
    dimension: "Root Cause Analysis",
    observability: {
      title: "Manual / semi-automated",
      example: "Engineer identifies DB contention",
    },
    aiops: {
      title: "Automated & causal",
      example: "AI identifies single root cause",
    },
  },
  {
    dimension: "Alerting",
    observability: {
      title: "Threshold-based",
      example: "Multiple noisy alerts",
    },
    aiops: {
      title: "Intelligent & contextual",
      example: "200 alerts reduced to 1",
    },
  },
  {
    dimension: "Prediction",
    observability: {
      title: "Reactive",
      example: "Issue detected after user impact",
    },
    aiops: {
      title: "Predictive",
      example: "Failure predicted in advance",
    },
  },
  {
    dimension: "Automation",
    observability: {
      title: "Minimal / rule-based",
      example: "Scripts run post-incident",
    },
    aiops: {
      title: "Advanced & AI-driven",
      example: "Auto-scaling, rerouting",
    },
  },
  {
    dimension: "Response Speed",
    observability: {
      title: "Human-dependent",
      example: "MTTR depends on availability",
    },
    aiops: {
      title: "Machine-speed",
      example: "Action in seconds",
    },
  },
  {
    dimension: "Skill Dependency",
    observability: {
      title: "High",
      example: "Needs senior SREs",
    },
    aiops: {
      title: "Reduced",
      example: "AI assists teams",
    },
  },
  {
    dimension: "Outcome",
    observability: {
      title: "Insights & explanation",
      example: "Clear technical understanding",
    },
    aiops: {
      title: "Action & optimization",
      example: "Incident prevented or resolved",
    },
  },
  {
    dimension: "Business Context",
    observability: {
      title: "Limited / manual",
      example: "Impact estimated post-incident",
    },
    aiops: {
      title: "Real-time & quantified",
      example: "Revenue, SLA, users impacted shown",
    },
  },
  {
    dimension: "Business Outcomes",
    observability: {
      title: "Operational visibility & risk awareness",
      example:
        "Better understanding of system health, fewer blind spots, improved transparency for IT leadership",
    },
    aiops: {
      title: "Measurable business value & resilience",
      example:
        "40–70% MTTR reduction, 60–80% alert reduction, downtime cost avoidance, SLA/SLO protection, improved Customer experience",
    },
  },
  {
    dimension: "Role in Maturity",
    observability: {
      title: "Mandatory foundation",
      example: "Baseline capability for modern IT",
    },
    aiops: {
      title: "Strategic differentiator",
      example: "Enables autonomous, resilient operations",
    },
  },
  {
    dimension: "Risk if Used Alone",
    observability: {
      title: "Alert fatigue, slow recovery",
      example: "Teams overwhelmed by data",
    },
    aiops: {
      title: "Blind automation risk",
      example: "Poor data can cause wrong actions",
    },
  },
];

const platforms = [
  {
    name: "Dynatrace",
    bullets: [
      "AI-native full-stack observability",
      "Automatic discovery & dependency mapping",
      "Davis AI for root cause analysis",
      "Best for: Complex cloud-native environments",
    ],
  },
  {
    name: "Splunk",
    bullets: [
      "Log analytics & security intelligence",
      "SPL for advanced querying",
      "SOAR integration",
      "Best for: Security-focused observability, large-scale log management",
    ],
  },
  {
    name: "Datadog",
    bullets: [
      "Cloud & microservices monitoring",
      "600+ integrations",
      "APM, infrastructure, logs unified",
      "Best for: Cloud-native, DevOps teams",
    ],
  },
  {
    name: "Elastic",
    bullets: [
      "Search-driven observability",
      "ELK stack (Elasticsearch, Logstash, Kibana)",
      "Custom analytics at scale",
      "Best for: Custom observability, security analytics",
    ],
  },
  {
    name: "NETSCOUT",
    bullets: [
      "Deep network intelligence",
      "Packet-level visibility",
      "Service assurance",
      "Best for: Network-centric operations, telecom, ISPs",
    ],
  },
  {
    name: "Keysight",
    bullets: [
      "Packet, traffic & performance engineering",
      "Network TAPs & packet brokers",
      "Application performance analytics",
      "Best for: High-fidelity network analysis, security forensics",
    ],
  },
  {
    name: "Zabbix",
    bullets: [
      "Infrastructure, network & legacy monitoring",
      "Open-source, highly customizable",
      "Agent-based & agentless monitoring",
      "Best for: Traditional infrastructure, budget-conscious deployments",
    ],
  },
];

export default function TechnologyStackPage() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="pt-6 sm:pt-10">
        <MotionReveal>
          <div className="grid gap-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
              Vendor-agnostic. Outcome-driven.
            </div>
            <div>
              <h1 className="font-[var(--font-display)] text-4xl sm:text-5xl tracking-tight text-white">
                Vendor-Agnostic. Outcome-Driven.
              </h1>
              <p className="mt-4 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
                We select the best tool for each signal type, unified by intelligence at the top.
              </p>
            </div>
          </div>
        </MotionReveal>
      </div>

      <section className="mt-10 sm:mt-14">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">
              Dimension-based comparison
            </div>
            <div className="mt-3 font-[var(--font-display)] text-2xl tracking-tight text-white">
              Observability vs AIOps
            </div>
            <div className="mt-2 text-sm text-[var(--text-secondary)]">
              Observability provides visibility. AIOps converts that visibility into decisions and automated action.
            </div>

            <div className="mt-6 hidden overflow-hidden rounded-2xl border border-white/10 bg-[#0b1220]/60 lg:block">
              <div className="grid grid-cols-[0.22fr_0.39fr_0.39fr]">
                <div className="border-b border-white/8 px-5 py-4 text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">
                  Dimension
                </div>
                <div className="border-b border-white/8 px-5 py-4">
                  <div className="text-sm font-medium text-white">Observability</div>
                  <div className="mt-1 text-xs text-[var(--text-secondary)]">Observability – Example / Explanation</div>
                </div>
                <div className="border-b border-white/8 px-5 py-4">
                  <div className="text-sm font-medium text-white">AIOps</div>
                  <div className="mt-1 text-xs text-[var(--text-secondary)]">AIOps – Example / Explanation</div>
                </div>

                {comparisonRows.map((row) => (
                  <div key={row.dimension} className="contents">
                    <div className="border-b border-white/8 px-5 py-4 text-sm text-white/90">
                      {row.dimension}
                    </div>
                    <div className="border-b border-white/8 px-5 py-4">
                      <div className="text-sm text-white">{row.observability.title}</div>
                      <div className="mt-2 text-sm text-[var(--text-secondary)]">{row.observability.example}</div>
                    </div>
                    <div className="border-b border-white/8 px-5 py-4">
                      <div className="text-sm text-white">{row.aiops.title}</div>
                      <div className="mt-2 text-sm text-[var(--text-secondary)]">{row.aiops.example}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 grid gap-3 lg:hidden">
              {comparisonRows.map((row) => (
                <div key={row.dimension} className="rounded-3xl border border-white/10 bg-white/3 p-5">
                  <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">{row.dimension}</div>
                  <div className="mt-4 grid gap-3">
                    <div className="rounded-2xl border border-white/10 bg-white/3 p-4">
                      <div className="text-sm font-medium text-white">Observability</div>
                      <div className="mt-2 text-sm text-white">{row.observability.title}</div>
                      <div className="mt-2 text-sm text-[var(--text-secondary)]">{row.observability.example}</div>
                    </div>
                    <div className="rounded-2xl border border-[rgba(0,212,255,0.22)] bg-white/3 p-4">
                      <div className="text-sm font-medium text-white">AIOps</div>
                      <div className="mt-2 text-sm text-white">{row.aiops.title}</div>
                      <div className="mt-2 text-sm text-[var(--text-secondary)]">{row.aiops.example}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-10 sm:mt-14">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[var(--accent-primary)]">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <div className="font-[var(--font-display)] text-xl tracking-tight text-white">
                  Architecture Layers
                </div>
                <div className="text-sm text-[var(--text-secondary)]">
                  Telemetry → Correlation → AI/ML → Automation → Executive Visualization
                </div>
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-[#0b1220]/60">
              <div className="grid">
                {[
                  "EXECUTIVE DASHBOARDS & VISUALIZATION",
                  "AUTOMATION & REMEDIATION",
                  "AI/ML & AIOps ENGINE",
                  "CORRELATION & ANALYTICS",
                  "TELEMETRY COLLECTION",
                ].map((layer) => (
                  <div key={layer} className="flex items-center justify-between gap-4 border-b border-white/8 px-5 py-4 last:border-b-0">
                    <div className="text-sm font-medium text-white">{layer}</div>
                    <div className="h-2 w-20 rounded-full bg-white/5">
                      <div className="h-full w-2/3 rounded-full bg-[var(--accent-primary)] opacity-60" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/8 p-5">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {platforms.map((p) => (
                    <div key={p.name} className="rounded-2xl border border-white/10 bg-white/3 px-4 py-3 text-sm text-white">
                      {p.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-12 sm:mt-16">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {platforms.map((p, idx) => (
            <MotionReveal key={p.name} delay={Math.min(0.35, idx * 0.04)}>
              <div className="glass-panel hover-lift rounded-3xl p-6">
                <div className="font-[var(--font-display)] text-xl tracking-tight text-white">{p.name}</div>
                <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)] opacity-75" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </MotionReveal>
          ))}
        </div>
      </section>

      <section className="mt-16 sm:mt-20">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-8 sm:p-10">
            <div className="flex items-start gap-4">
              <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[var(--accent-primary)]">
                <Quote className="h-5 w-5" />
              </div>
              <div>
                <div className="font-[var(--font-display)] text-2xl tracking-tight text-white">
                  We don’t sell tools. We architect outcomes.
                </div>
                <p className="mt-3 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
                  The right tool for each signal type, orchestrated by unified intelligence.
                </p>
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <div className="h-12" />
    </div>
  );
}
